//----------------CORE-----------------//
// Fleet Reports layout (license-gated Fleet KPI dashboard, 2026-07-14).
//
// BUILT, not copied from layouts/roi.js. roi.js is 100% driven by
// metaDataValue.appPaths, which comes ONLY from the rolesMS role doc. Fleet
// Reports is gated by a LICENCE capability, not a role module, so appPaths has
// no /fleetReports/ entries — a roi.js clone would render zero tabs and then
// crash on `output[0].path` of an empty array. The route table below is LOCAL.
//
// THE QUERY IS HOISTED HERE, above the <Switch>, together with the controls bar:
// sub-tab navigation unmounts the routed component, so a query living inside a
// tab would refire on every tab change. With the query here (and
// refetchOnMountOrArgChange deliberately omitted from services/reporting.js), a
// user who clicks Load once and then cycles Summary <-> Efficiency <-> Score
// issues EXACTLY ONE GET /aggregation/fleet-report.
import React from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
//----------------MUI-----------------//
import { makeStyles } from "@mui/styles";
import { Card } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import SpeedIcon from "@mui/icons-material/Speed";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import PaidIcon from "@mui/icons-material/Paid";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardReturnOutlinedIcon from "@mui/icons-material/KeyboardReturnOutlined";
//----------------EXTERNAL COMPONENTS-----------------//
import Navbar from "components/Navbars/Navbar.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import Loader from "components/Progress";
import useFleetKpiConfig from "hooks/useFleetKpiConfig";
import { useGetFleetReportQuery } from "services/reporting";
import { setFleetReport } from "rtkSlices/reportingSlice";
import Controls from "components/Fleet Reports/Controls";
import Summary from "components/Fleet Reports/Summary";
import MetricSection from "components/Fleet Reports/MetricSection";
import IdlingSection from "components/Fleet Reports/IdlingSection";
import BehaviorSection from "components/Fleet Reports/BehaviorSection";
import UnpluggedSection from "components/Fleet Reports/UnpluggedSection";
import FuelCostSection from "components/Fleet Reports/FuelCostSection";
import SettingsSection from "components/Fleet Reports/SettingsSection";

const useStyles = makeStyles({
  root: { backgroundColor: "#eeeeee" },
  component: {
    backgroundColor: "#eeeeee",
    position: "absolute",
    top: "64px",
    left: "60px",
    padding: "20px 15px 20px 15px",
    minHeight: "calc(100vh - 64px)",
    minWidth: "calc(100vw - 60px)",
  },
  centre: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 220px)",
  },
});

export default function FleetReportsLayout(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const metaDataValue = useSelector((state) => state.metaData);
  const form = useSelector((state) => state.fleetReport);
  const fleet = useFleetKpiConfig();
  const location = useLocation();

  // Seed the window from the SERVER's IN-PROGRESS week ("this week so far") AND
  // auto-load it, so the dashboard lands on the latest state with no click. Fall
  // back to the last COMPLETE week if an older backend didn't send currentWindow
  // (deploy-skew safety). BOTH come from the server in the tenant's zone — a
  // browser-computed default would pick a different week for a viewer in another
  // timezone.
  //
  // `loaded: true` here is what fires the report query (it is `skip`-gated on
  // form.loaded). This runs ONCE per mount (guarded on !form.startDate) — NOT on
  // every sub-tab change, because the query is hoisted above the <Switch> and
  // refetchOnMountOrArgChange is deliberately off. The backend fronts this scan
  // with a Redis cache + single-flight lease + per-tenant rate limit, so concurrent
  // users on a tenant coalesce onto one build. After this first load, switching
  // weeks AUTO-LOADS too (onChange preserves `loaded`); the picker offers a bounded
  // ~7 windows, each cached 300s, so switching cannot outrun the rate limit.
  React.useEffect(() => {
    if (fleet.enabled && !form.startDate) {
      const seed =
        fleet.currentWindow && fleet.currentWindow.startDate ? fleet.currentWindow : fleet.defaultWindow;
      if (seed && seed.startDate) dispatch(setFleetReport({ ...seed, loaded: true }));
    }
  }, [fleet.enabled, fleet.currentWindow, fleet.defaultWindow, form.startDate, dispatch]);

  const { data, isFetching, error, refetch } = useGetFleetReportQuery(
    {
      token: window.localStorage.getItem("token"),
      startDate: form.startDate,
      endDate: form.endDate,
    },
    { skip: !fleet.enabled || !form.loaded || !form.startDate || !form.endDate }
  );

  const report = data && data.payload;

  const onLoad = () => {
    if (!form.loaded) dispatch(setFleetReport({ loaded: true }));
    else refetch(); // same args = an explicit "recompute now" gesture
  };

  // ── Gating. The REAL boundary is requireFleetKpi server-side; this is
  // cosmetic. Tri-state so a hard refresh on /fleetReports survives the
  // config round-trip instead of bouncing to the error page.
  if (fleet.loading) {
    return (
      <div className={classes.root}>
        <div className={classes.centre}>
          <Loader />
        </div>
      </div>
    );
  }
  if (!fleet.enabled) return <Redirect to="/solutions" />;
  // Fleet Reports rides on EXISTING Analytics access (product decision: no separate
  // RBAC). A user without the Analytics module is bounced — mirrors the NavbarLinks
  // tile gate so a direct/bookmarked /fleetReports is blocked too. Guarded on `apps`
  // being loaded (Array) so a hard refresh doesn't redirect before the role modules
  // arrive. FE-side only; the tenant capability (requireFleetKpi) is the real boundary.
  if (Array.isArray(metaDataValue.apps) && !metaDataValue.apps.some((a) => a.name === "Analytics"))
    return <Redirect to="/solutions" />;

  const backButton = {
    name: "Back",
    // localStorage.getItem returns null, and `null != ""` is TRUE — the roi.js
    // check ships `/solutions/null`. Use a truthiness test.
    path: window.localStorage.getItem("currentService") || "catalogue",
    layout: "/solutions/",
    icon: KeyboardReturnOutlinedIcon,
  };

  // Section order (owner decision 2026-07-23): fuel trio together (efficiency,
  // consumption, cost), then the driver pair (score, behavior), idling, and
  // unplugged last.
  const fleetRoutes = [
    { name: "Summary", path: "summary", layout: "/fleetReports/", icon: DashboardIcon,
      component: <Summary report={report} isFetching={isFetching} /> },
    { name: "Fuel Efficiency", path: "fuelEfficiency", layout: "/fleetReports/", icon: SpeedIcon,
      component: <MetricSection metric="efficiency" title="Fuel Efficiency" unit="L/100km" better="lower" hasDaily={false} agg="average" report={report} /> },
    { name: "Fuel Consumption", path: "fuelConsumption", layout: "/fleetReports/", icon: LocalGasStationIcon,
      component: <MetricSection metric="consumption" title="Fuel Consumption" unit="L" better="lower" hasDaily report={report} /> },
    // Fuel Cost is ALWAYS visible (2026-07-23): with no price configured its
    // empty state points the user to Settings, where the price is set.
    { name: "Fuel Cost", path: "fuelCost", layout: "/fleetReports/", icon: PaidIcon,
      component: <FuelCostSection report={report} /> },
    { name: "Driving Score", path: "drivingScore", layout: "/fleetReports/", icon: EmojiEventsIcon,
      component: <MetricSection metric="drivingScore" title="Driving Score" unit="/ 10" better="higher" hasDaily agg="average" report={report} /> },
    { name: "Driving Behavior", path: "drivingBehavior", layout: "/fleetReports/", icon: WarningAmberIcon,
      component: <BehaviorSection report={report} /> },
    { name: "Idling Time", path: "idling", layout: "/fleetReports/", icon: HourglassEmptyIcon,
      component: <IdlingSection report={report} /> },
    { name: "Unplugged Alerts", path: "unplugged", layout: "/fleetReports/", icon: PowerOffIcon,
      component: <UnpluggedSection report={report} /> },
    // Dashboard-editable settings (fuel price + vehicle selection, 2026-07-23).
    { name: "Settings", path: "settings", layout: "/fleetReports/", icon: SettingsIcon,
      component: <SettingsSection /> },
  ];

  // ── The five states. None of these is optional polish.
  const body = () => {
    // Settings must be reachable WITHOUT a loaded report (it is where the fuel
    // price is first set), so it bypasses every report state below. Lowercased
    // exact-or-slash match: react-router matches paths case-insensitively, and a
    // bare startsWith would also swallow /fleetReports/settingsanything.
    const path = location.pathname.toLowerCase();
    if (path === "/fleetreports/settings" || path.startsWith("/fleetreports/settings/")) {
      return <SettingsSection />;
    }
    // Auto-load seeds the window on mount (see the effect above); show the loader
    // during that one-frame gap instead of flashing the "choose a week" empty
    // state. Guarded on a window actually being available so a backend that
    // somehow sent neither window falls through to the empty state below (never
    // hangs on the loader).
    if (
      !form.startDate &&
      ((fleet.currentWindow && fleet.currentWindow.startDate) || (fleet.defaultWindow && fleet.defaultWindow.startDate))
    ) {
      return (
        <div className={classes.centre}>
          <Loader />
        </div>
      );
    }
    if (isFetching) {
      return (
        <div className={classes.centre}>
          <Loader />
        </div>
      );
    }
    if (error) {
      // 403 => the capability is off (or was turned off mid-session): show the
      // permission state, NOT a generic error. Key on 403, never 404.
      const status = error.status || (error.data && error.data.code);
      const msg =
        status === 403
          ? "Fleet reporting is not enabled for your account."
          : status === 400
          ? (error.data && error.data.message) || "That window cannot be reported on."
          : status === 429
          ? "Too many report requests - please wait a moment and try again."
          : "The report could not be loaded. Please try again.";
      return (
        <Card style={{ padding: "40px 20px", textAlign: "center" }}>
          <p style={{ fontSize: "15px", color: "#c7c7c7", margin: 0 }}>{msg}</p>
        </Card>
      );
    }
    if (!form.loaded || !report) {
      return (
        <Card style={{ padding: "40px 20px", textAlign: "center" }}>
          <p style={{ fontSize: "15px", color: "#c7c7c7", margin: 0 }}>
            Choose a week and select <b>Load</b> to build the report.
          </p>
        </Card>
      );
    }
    // Empty week: telemetry exists for no vehicle at all in the window.
    const scanned = (report.meta && report.meta.docsScanned) || 0;
    if (scanned === 0) {
      return (
        <Card style={{ padding: "40px 20px", textAlign: "center" }}>
          <p style={{ fontSize: "15px", color: "#c7c7c7", margin: 0 }}>
            No telemetry was recorded for this fleet between {report.window.startDate} and {report.window.endDate}.
          </p>
        </Card>
      );
    }
    return (
      <Switch>
        {fleetRoutes.map((r) => (
          <Route path={r.layout + r.path} key={r.path}>
            {r.component}
          </Route>
        ))}
        <Redirect to="/fleetReports/summary" />
      </Switch>
    );
  };

  return (
    <div className={classes.root}>
      <Navbar
        routes={[
          ...(metaDataValue.appPaths || []),
          { name: "Fleet Reports", path: "", layout: "/fleetReports" },
        ]}
        history={props.history}
        services={metaDataValue.services}
      />
      <Sidebar routes={[backButton, ...fleetRoutes]} />
      <div className={classes.component}>
        <Controls
          config={fleet}
          startDate={form.startDate}
          endDate={form.endDate}
          loaded={form.loaded}
          isFetching={isFetching}
          // Download is offered only when a report is actually on screen (loaded,
          // fetched, present) — never for a window that has not been built.
          canDownload={form.loaded && !!report && !isFetching && !error}
          // Once a report has been loaded (the current week auto-loads on
          // mount), switching weeks AUTO-LOADS: preserve `loaded` instead of
          // resetting it to false. The query is keyed on startDate/endDate, so a
          // new window fetches while a previously-viewed one is served from the
          // RTK cache (keepUnusedDataFor 900s) with no refetch. Before the first
          // load (defensive: auto-load somehow didn't run) `loaded` stays false,
          // so an arbitrary pick still waits for an explicit Load — matching
          // "once loaded once, switching auto-loads".
          onChange={(w) => dispatch(setFleetReport({ ...w, loaded: form.loaded }))}
          onLoad={onLoad}
        />
        {body()}
      </div>
    </div>
  );
}
